import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../api/client.js";
import type { Property, PropertyType } from "./api.js";

/**
 * Property Insights API contract — kept in lockstep with
 * /customer/api/src/domain/property-insights.ts. Insights are system-derived
 * facts (lot size, home sqft, mowable lawn area, property type), enriched
 * asynchronously after a property is added or re-addressed. They ride along
 * on the property DETAIL response as an additive `insights` key.
 */

export type InsightsStatus = "pending" | "complete" | "partial" | "failed" | "out_of_area";

/** Where a derived value came from; "user" means the owner corrected it. */
export type InsightSource =
  | "scott_county_gis"
  | "rock_island_county_gis"
  | "fema_usa_structures"
  | "rentcast"
  | "heuristic"
  | "vision"
  | "user";

export interface MowableAreaBand {
  low: number;
  high: number;
}

export interface PropertyInsights {
  propertyId: string;
  ownerSub: string;
  schemaVersion: 1;
  status: InsightsStatus;
  county?: string;
  // Core facts.
  lotSizeSqft?: number;
  lotSizeAcres?: number;
  homeSqft?: number;
  mowableAreaSqft?: number;
  mowableAreaBand?: MowableAreaBand;
  derivedPropertyType?: PropertyType;
  /** Derived type disagrees with the customer-selected propertyType. */
  typeMismatch?: boolean;
  // Bonus facts.
  yearBuilt?: number;
  /**
   * Short-lived presigned URL (~5 min) for the stored NAIP aerial crop —
   * present on detail responses once enrichment has stored one. Fine for a
   * page view; the detail query refetches on mount so it never goes stale
   * for long.
   */
  aerialUrl?: string;
  /** NAIP acquisition datetime — shown as the imagery vintage in the UI. */
  aerialCapturedAt?: string;
  /** Per-field provenance, e.g. { lotSizeSqft: "scott_county_gis" }. */
  fieldSources?: Record<string, InsightSource>;
  /** Fields the OWNER has edited — locked against future auto-estimation. */
  overriddenFields?: Record<string, boolean>;
  enrichedAt?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * PATCH /properties/{id}/insights body. A number sets the field AND locks it
 * against future auto-estimation; `null` clears both the value and the lock
 * so the next enrichment run may fill it again. Send only changed fields.
 */
export interface UpdateInsightsInput {
  lotSizeSqft?: number | null;
  homeSqft?: number | null;
  mowableAreaSqft?: number | null;
  yearBuilt?: number | null;
}

export const SQFT_PER_ACRE = 43_560;

export function useUpdateInsights(propertyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateInsightsInput) =>
      apiFetch<PropertyInsights>(`/properties/${propertyId}/insights`, {
        method: "PATCH",
        json: input,
      }),
    onSuccess: (updated) => {
      // Insights ride along on the property detail response — merge the fresh
      // record into the cached detail so the card updates without a refetch.
      // The PATCH response includes aerialUrl, but merge over the previous
      // insights anyway so no transient field can ever be stripped by a
      // response that omits it.
      qc.setQueryData<Property>(["properties", propertyId], (prev) =>
        prev ? { ...prev, insights: { ...prev.insights, ...updated } } : prev,
      );
    },
  });
}
